import React from 'react';
import { Settings } from 'lucide-react';
import type { SectionPreviewProps } from '@/types/preview';
import type { PageSection } from '@/types/page';

// Import existing section components
import { HeroSection } from './HeroSection';
import { StatsSection } from './StatsSection';
import { FeaturedPropertiesSection } from './FeaturedPropertiesSection';
import { CTASection } from './CTASection';
import { TestimonialsSection } from './TestimonialsSection';
import { FAQSection } from './FAQSection';
import { TeamSection } from './TeamSection';
import { ServicesSection } from './ServicesSection';
import { BlogSection } from './BlogSection';
import { GallerySection } from './GallerySection';
import { ContactFormSection } from './ContactFormSection';

/**
 * Component for rendering individual page sections in preview mode
 * Uses the existing section components from the system
 */
export const SectionPreview: React.FC<SectionPreviewProps> = ({ 
  section, 
  isEnabled, 
  tenantConfig,
  viewport = 'desktop',
  className = '' 
}) => {
  
  /**
   * Render section content based on type using existing components
   */
  const renderSectionContent = () => {
    // If section is disabled and we're not showing disabled sections, return null
    if (!isEnabled && !section.enabled) {
      return null;
    }

    try {
      switch (section.type) {
        case 'HERO':
          return <HeroSection section={section} className={className} />;

        case 'STATS':
          return <StatsSection section={section} className={className} />;

        case 'FEATURED_PROPERTIES':
          return <FeaturedPropertiesSection section={section} className={className} />;

        case 'CTA':
          return <CTASection section={section} className={className} />;

        case 'TESTIMONIALS':
          return <TestimonialsSection section={section} className={className} />;

        case 'FAQ':
          return <FAQSection section={section} className={className} />;

        case 'TEAM':
          return <TeamSection section={section} className={className} />;

        case 'SERVICES':
          return <ServicesSection section={section} className={className} />;

        case 'BLOG':
        case 'BLOG_PREVIEW':
          return <BlogSection section={section} className={className} />;

        case 'GALLERY':
          return <GallerySection section={section} className={className} />;

        case 'CONTACT':
        case 'CONTACT_FORM':
          return <ContactFormSection section={section} className={className} />;

        case 'INTRO':
        case 'MISSION':
        case 'TIMELINE':
        case 'CULTURE':
        case 'CERTIFICATIONS':
          // These are handled by the page mapper and rendered in specific page layouts
          return renderPlaceholderSection(section, section.type);

        default:
          console.warn(`Unknown section type: ${section.type}`);
          return renderPlaceholderSection(section, `Sección ${section.type}`);
      }
    } catch (error) {
      console.error(`Error rendering section ${section.type}:`, error);
      return renderErrorSection(section, error);
    }
  };

  /**
   * Render placeholder for unimplemented sections
   */
  const renderPlaceholderSection = (section: PageSection, typeName: string) => {
    return (
      <div className="py-16 bg-gray-100 dark:bg-gray-700">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="text-gray-400 mb-4">
            <Settings className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {typeName}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {section.title || `Vista previa de ${typeName}`}
          </p>
          <div className="text-xs text-gray-500 dark:text-gray-500 bg-gray-200 dark:bg-gray-600 px-3 py-1 rounded-full inline-block">
            Tipo: {section.type} • ID: {section.id}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render error state for sections that failed to render
   */
  const renderErrorSection = (section: PageSection, error: any) => {
    return (
      <div className="py-16 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="text-red-500 mb-4">
            <Settings className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-red-900 dark:text-red-100 mb-2">
            Error en Sección
          </h3>
          <p className="text-red-600 dark:text-red-400 mb-2">
            No se pudo renderizar la sección: {section.title || section.type}
          </p>
          <div className="text-xs text-red-500 bg-red-200 dark:bg-red-800 px-3 py-1 rounded-full inline-block">
            Error: {error?.message || 'Error desconocido'}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Get responsive classes based on viewport
   */
  const getViewportClasses = () => {
    const baseClasses = 'relative transition-all duration-300';
    
    switch (viewport) {
      case 'mobile':
        return `${baseClasses} max-w-sm mx-auto`;
      case 'tablet':
        return `${baseClasses} max-w-3xl mx-auto`;
      case 'desktop':
      default:
        return `${baseClasses} w-full`;
    }
  };

  /**
   * Get disability indicator classes
   */
  const getDisabilityClasses = () => {
    if (!isEnabled || !section.enabled) {
      return 'opacity-50 relative';
    }
    return '';
  };

  const content = renderSectionContent();
  
  // If section is disabled and content is null, don't render anything
  if (!content) {
    return null;
  }

  return (
    <div className={`${getViewportClasses()} ${getDisabilityClasses()}`}>
      {/* Disabled indicator */}
      {(!isEnabled || !section.enabled) && (
        <div className="absolute top-4 right-4 z-10 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
          Deshabilitada
        </div>
      )}
      
      {/* Section order indicator (only in edit mode) */}
      <div className="absolute top-4 left-4 z-10 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium shadow-lg opacity-75">
        #{section.order}
      </div>
      
      {/* Section content */}
      {content}
    </div>
  );
};

/**
 * Utility function to check if a section type is supported
 */
export const isSectionTypeSupported = (type: string): boolean => {
  const supportedTypes = [
    'HERO',
    'STATS',
    'FEATURED_PROPERTIES',
    'CTA',
    'TESTIMONIALS',
    'FAQ',
    'TEAM',
    'SERVICES',
    'BLOG',
    'BLOG_PREVIEW',
    'GALLERY',
    'CONTACT',
    'CONTACT_FORM',
    'INTRO',
    'MISSION',
    'TIMELINE',
    'CULTURE',
    'CERTIFICATIONS'
  ];

  return supportedTypes.includes(type);
};

/**
 * Get all enabled sections sorted by order
 */
export const getEnabledSections = (sections: PageSection[]): PageSection[] => {
  return sections
    .filter(section => section.enabled)
    .sort((a, b) => a.order - b.order);
};

/**
 * Render all sections for a page
 */
export const renderPageSections = (
  sections: PageSection[], 
  options: {
    showDisabled?: boolean;
    viewport?: 'desktop' | 'tablet' | 'mobile';
    tenantConfig?: any;
  } = {}
): React.ReactElement[] => {
  const { showDisabled = false, viewport = 'desktop', tenantConfig } = options;
  
  const sectionsToRender = showDisabled 
    ? sections.sort((a, b) => a.order - b.order)
    : getEnabledSections(sections);
  
  return sectionsToRender.map((section, index) => (
    <SectionPreview 
      key={section.id} 
      section={section}
      isEnabled={section.enabled || showDisabled}
      viewport={viewport}
      tenantConfig={tenantConfig}
      className={index === 0 ? 'first-section' : ''}
    />
  ));
};